/**
 * Start a timer on the same work as a past entry — Toggl's "continue".
 *
 * The entry's project, task, description, activity and kind carry over, so
 * picking up where you left off is one tap rather than re-entering the same
 * five fields. A timer already running is stopped and logged first: pressing
 * ▶ on another row means "I've moved on", not "throw away what I was timing".
 */

import { useCallback } from "react";

import { useTimer } from "@/context/TimerContext";
import { useToast } from "@/context/ToastContext";
import { useProjects } from "@/hooks/useData";
import { kindIn } from "@/lib/billing";
import type { TimeEntry } from "@/lib/types";

export function useContinueEntry(): (entry: TimeEntry) => Promise<void> {
  const timer = useTimer();
  const toast = useToast();
  const { data: projects = [] } = useProjects();

  return useCallback(async (entry: TimeEntry) => {
    if (timer.running) await timer.stop();
    timer.start({
      projectId: entry.project || undefined,
      itemId: entry.item || undefined,
      clientId: entry.client || undefined,
      category: entry.category || undefined,
      note: entry.note ?? "",
      kind: kindIn(entry, projects),
    });
    toast(`Timer started${entry.note ? ` — ${entry.note}` : ""}`);
  }, [timer, toast, projects]);
}
